import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useLineLiff from 'hooks/LineLiff';
import request from 'pages/toyotaPremiumOfficialAccount/utils/request';
import ConversationItem from '../../../components/toyotaPremium/ConversationItem';

const getSalesProfile = async () => {
  const response = await request.get('/core/is_sales');
  return response.data.data?.sales || null;
};

const getSalesConversations = async () => {
  const response = await request.get('/conversations_sales');
  return response.data.data || [];
};

const formatTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const isSameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (isSameDay) {
    return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}`;
};

const isUnread = (conversation) => {
  const lastMessage = conversation.last_message;
  if (!lastMessage || lastMessage.author?.is_sales) return false;
  if (!conversation.last_read_time) return true;
  return lastMessage.create_time > conversation.last_read_time;
};

const sortConversations = (list) =>
  [...list].sort((a, b) => {
    const aTime = a.last_message?.create_time;
    const bTime = b.last_message?.create_time;
    if (aTime && bTime) return bTime.localeCompare(aTime);
    if (aTime) return -1;
    if (bTime) return 1;
    return (b.id || 0) - (a.id || 0);
  });

function ConversationList() {
  const navigate = useNavigate();
  const { userInfo } = useLineLiff(process.env.REACT_APP_LIFF_ID_CONSULTATION);
  const [conversations, setConversations] = useState([]);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const salesName = profile?.name || userInfo?.displayName || '';
  const officeName = profile?.sales_office?.name || '';

  useEffect(() => {
    if (!userInfo) return undefined;
    let cancelled = false;
    let redirected = false;

    const fetchConversations = async () => {
      try {
        const sales = await getSalesProfile();
        if (cancelled) return;
        if (!sales) {
          redirected = true;
          navigate('/ToyotaPremium/consultation/branch-list');
          return;
        }
        const loaded = await getSalesConversations();
        if (cancelled) return;
        setProfile(sales);
        setConversations(sortConversations(loaded));
      } catch {
        // toast already shown in request.js
      } finally {
        if (!cancelled && !redirected) setIsLoading(false);
      }
    };

    fetchConversations();
    return () => {
      cancelled = true;
    };
  }, [navigate, userInfo]);

  return (
    <div className="conversation-list" aria-busy={isLoading}>
      <div className="conversation-list__header">
        <div className="conversation-list__profile">
          <div className="conversation-list__avatar">
            <div className="conversation-list__avatar-img"></div>
          </div>
          <div className="conversation-list__user-info">
            <div className="conversation-list__user-name">{salesName}</div>
            <div className="conversation-list__user-description">
              {officeName}
            </div>
          </div>
        </div>
      </div>
      <div className="conversation-list__items-container">
        {isLoading ? (
          <p className="conversation-list__status" role="status">
            對話載入中
          </p>
        ) : !conversations.length ? (
          <p className="conversation-list__status" role="status">
            目前沒有對話
          </p>
        ) : (
          <div className="conversation-list__items">
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                userName={conversation.client?.name || ''}
                location={conversation.sales_office?.region || ''}
                message={conversation.last_message?.message || '尚無訊息'}
                time={formatTime(conversation.last_message?.create_time)}
                isUnread={isUnread(conversation)}
                onClick={() =>
                  navigate(
                    `/ToyotaPremium/consultation/sales-rep-chatroom/${conversation.id}`
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ConversationList;
